import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { sourcingAPI, getErrorDetail } from '../../lib/api';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Loader2, Send, AlertTriangle } from 'lucide-react';
import { DRAFT_STATUS_BADGE, VENDOR_TIER_BADGE, VENDOR_TIER_LABEL, badgeClass } from './sourcingUtils';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function formatDdMon(isoDate) {
  // Parse the date string's own calendar date directly — new Date(iso) shifts by the
  // browser's local timezone and can show the wrong day near midnight.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate || '');
  if (!m) return isoDate;
  return `${parseInt(m[3], 10)} ${MONTH_NAMES[parseInt(m[2], 10) - 1]}`;
}

export default function MyDraftsTab() {
  const { canAccess } = useAuth();
  const canEdit = canAccess('SOURCING', 'EDIT');
  const [loading, setLoading] = useState(true);
  // "Mine" vs "My products" round (3 Nov 2026): remembered per browser, validated
  // against what this user is allowed to see (a stale/foreign 'all' would 403).
  const [view, setView] = useState(() => {
    const allowed = canEdit ? ['mine', 'products', 'team', 'all'] : ['mine', 'products', 'team'];
    try {
      const saved = localStorage.getItem('sourcing_drafts_view');
      return allowed.includes(saved) ? saved : 'mine';
    } catch { return 'mine'; }
  });
  const [tier, setTier] = useState('');
  // My Drafts order round (3 Nov 2026): default to OPEN only, remembered per browser.
  const [status, setStatus] = useState(() => {
    try { return localStorage.getItem('sourcing_drafts_status_filter') || 'OPEN'; } catch { return 'OPEN'; }
  });
  const [drafts, setDrafts] = useState([]);
  const [caps, setCaps] = useState(null);
  const [selected, setSelected] = useState({}); // draftId -> Set(lineId)
  const [qtyOverrides, setQtyOverrides] = useState({}); // lineId -> qty
  const [priceLines, setPriceLines] = useState({}); // lineId -> bool ("Send our price" ticked)
  const [priceOverrides, setPriceOverrides] = useState({}); // lineId -> string price
  const [sendDraft, setSendDraft] = useState(null);
  const [sendPreview, setSendPreview] = useState(null); // { on_behalf, assignee_name, login_used, used_own_login }
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [lastBlocked, setLastBlocked] = useState(null);
  const [reassignDraft, setReassignDraft] = useState(null);
  const [reassignQuery, setReassignQuery] = useState('');
  const [reassignResults, setReassignResults] = useState([]);

  const load = useCallback(async (v = view, t = tier, s = status) => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([sourcingAPI.listDrafts(v, s, t), sourcingAPI.getCapabilities()]);
      setDrafts(d.data.drafts);
      setCaps(c.data);
      // Item 9 (25 Feb 2026, Lex's quota round — revised): low-qty lines are real draft
      // lines now, just unticked by default — pre-select every normal line so sending
      // is one click, while low-qty lines need a deliberate decision to include.
      setSelected((prev) => {
        const next = { ...prev };
        d.data.drafts.forEach((draft) => {
          if (draft.status === 'OPEN' && !(draft.id in next)) {
            next[draft.id] = new Set(draft.lines.filter((l) => !l.below_min_qty).map((l) => l.id));
          }
        });
        return next;
      });
    } catch (e) {
      toast.error('Failed to load drafts');
    } finally {
      setLoading(false);
    }
  }, [view, tier, status]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = (s) => {
    setStatus(s);
    try { localStorage.setItem('sourcing_drafts_status_filter', s); } catch { /* ignore */ }
  };

  const changeView = (v) => {
    setView(v);
    try { localStorage.setItem('sourcing_drafts_view', v); } catch { /* ignore */ }
  };

  const toggleLine = (draftId, lineId) => {
    setSelected((prev) => {
      const set = new Set(prev[draftId] || []);
      if (set.has(lineId)) set.delete(lineId); else set.add(lineId);
      return { ...prev, [draftId]: set };
    });
  };

  const togglePriceLine = (line, checked) => {
    setPriceLines((prev) => ({ ...prev, [line.id]: checked }));
    if (checked && priceOverrides[line.id] == null) {
      setPriceOverrides((prev) => ({ ...prev, [line.id]: line.target_price_usd != null ? String(line.target_price_usd) : '' }));
    }
  };

  const openSend = async (draft) => {
    const lineIds = Array.from(selected[draft.id] || []);
    if (lineIds.length === 0) { toast.error('Select at least one line to send'); return; }
    setSendDraft(draft);
    setNote('');
    setLastBlocked(null);
    setSendPreview(null);
    try {
      const res = await sourcingAPI.sendPreview(draft.id);
      setSendPreview(res.data);
    } catch (e) { /* preview is informational only — a failure here must never block sending */ }
  };

  const confirmSend = async () => {
    const lineIds = Array.from(selected[sendDraft.id] || []);
    const sendPriceLineIds = lineIds.filter((id) => priceLines[id]);
    for (const id of sendPriceLineIds) {
      const raw = priceOverrides[id];
      const num = Number(raw);
      if (raw === '' || raw == null || !Number.isFinite(num) || num <= 0) {
        toast.error('Enter a price greater than 0 for every line with "Send our price" ticked');
        return;
      }
      if (Math.round(num * 100) / 100 !== num) {
        toast.error('Price can have at most 2 decimal places');
        return;
      }
    }
    setSending(true);
    try {
      const overrides = {};
      lineIds.forEach((id) => { if (qtyOverrides[id] != null) overrides[id] = Number(qtyOverrides[id]); });
      const priceOverridesBody = {};
      sendPriceLineIds.forEach((id) => { priceOverridesBody[id] = Number(priceOverrides[id]); });
      const res = await sourcingAPI.sendDraft(sendDraft.id, {
        line_ids: lineIds, note: note || null, send_price_line_ids: sendPriceLineIds,
        price_overrides: priceOverridesBody, qty_overrides: overrides,
      });
      if (res.data.sent_lines.length > 0) toast.success(`RFQ sent to ${sendDraft.company_name} (${res.data.sent_lines.length} line(s))`);
      if (res.data.blocked_lines.length > 0) {
        setLastBlocked(res.data.blocked_lines);
        toast.warning(`${res.data.blocked_lines.length} line(s) on cooldown, not sent`);
      } else {
        setSendDraft(null);
      }
      load();
    } catch (e) {
      toast.error(getErrorDetail(e, 'Failed to send RFQ'));
    } finally {
      setSending(false);
    }
  };

  const openReassign = (draft) => {
    setReassignDraft(draft);
    setReassignQuery('');
    setReassignResults([]);
  };

  useEffect(() => {
    if (!reassignDraft) return;
    const timer = setTimeout(async () => {
      if (reassignQuery.length < 2) { setReassignResults([]); return; }
      try {
        const res = await api.get('/users/search', { params: { q: reassignQuery } });
        setReassignResults(res.data || []);
      } catch { setReassignResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [reassignQuery, reassignDraft]);

  const doReassign = async (userId) => {
    try {
      await sourcingAPI.reassignDraft(reassignDraft.id, userId);
      toast.success('Draft reassigned');
      setReassignDraft(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reassign');
    }
  };

  if (loading) return <div className="flex justify-center py-16" data-testid="drafts-loading"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="sourcing-drafts-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2" data-testid="drafts-view-switcher">
          <Button size="sm" variant={view === 'mine' ? 'default' : 'outline'} onClick={() => changeView('mine')} data-testid="drafts-view-mine-btn">Mine</Button>
          <Button size="sm" variant={view === 'products' ? 'default' : 'outline'} onClick={() => changeView('products')} data-testid="drafts-view-products-btn">My products</Button>
          <Button size="sm" variant={view === 'team' ? 'default' : 'outline'} onClick={() => changeView('team')} data-testid="drafts-view-team-btn">Team</Button>
          {canEdit && <Button size="sm" variant={view === 'all' ? 'default' : 'outline'} onClick={() => changeView('all')} data-testid="drafts-view-all-btn">All</Button>}
        </div>
        <div className="flex items-center gap-2">
          <select className="border border-border rounded-sm text-sm px-2 py-1.5 bg-background" value={status}
            onChange={(e) => changeStatus(e.target.value)} data-testid="drafts-status-filter">
            <option value="OPEN">Open</option>
            <option value="SENT">Sent</option>
            <option value="EXPIRED">Expired</option>
            <option value="">All</option>
          </select>
          <select className="border border-border rounded-sm text-sm px-2 py-1.5 bg-background" value={tier}
            onChange={(e) => setTier(e.target.value)} data-testid="drafts-tier-filter">
            <option value="">All tiers</option>
            <option value="TOP">Top vendor</option>
            <option value="REGULAR">Regular</option>
            <option value="PAST">Past vendor</option>
            <option value="NEW">New</option>
            <option value="UNMATCHED">Not in Zoho</option>
          </select>
          {caps && (
            <p className="text-sm text-muted-foreground" data-testid="drafts-caps-counter">
              {caps.my_rfqs_today}/{caps.my_send_cap} today · BrokerBin calls {caps.brokerbin_used_today}/{caps.brokerbin_cap}
            </p>
          )}
        </div>
      </div>

      {drafts.length === 0 && (
        <Card className="rounded-sm" data-testid="drafts-empty"><CardContent className="p-8 text-center text-sm text-muted-foreground">No drafts to show.</CardContent></Card>
      )}
      {drafts.map((draft) => (
        <Card key={draft.id} className={`rounded-sm ${draft.status === 'EXPIRED' ? 'opacity-60' : ''}`} data-testid={`draft-card-${draft.id}`}>
          <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
            <div className="min-w-0">
              <CardTitle className="text-base truncate">{draft.company_name}</CardTitle>
              {!!draft.po_value_60d && (
                <p className="text-xs text-muted-foreground" data-testid={`draft-vendor-value-${draft.id}`}>
                  Bought ${Math.round(draft.po_value_60d).toLocaleString()} in last 60 days, {draft.po_count_60d} PO{draft.po_count_60d === 1 ? '' : 's'}
                  {draft.last_po_date && `, last PO ${formatDdMon(draft.last_po_date)}`}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {draft.scorecard && draft.scorecard.rfqs_sent > 0 && (
                <Badge variant="outline" className="text-[11px]" data-testid={`draft-scorecard-${draft.id}`}>
                  {Math.round(draft.scorecard.reply_rate * 100)}% reply
                  {draft.scorecard.last_5_no_reply && <span className="text-destructive ml-1">· 5 no-reply in a row</span>}
                </Badge>
              )}
              <Badge variant="outline" className={badgeClass(DRAFT_STATUS_BADGE, draft.status)} data-testid={`draft-status-${draft.id}`}>{draft.status}</Badge>
              {view === 'mine' && !draft.assignee_id && (
                <Badge variant="outline" className="text-[11px] border-slate-400/40 bg-slate-500/10 text-slate-600" data-testid={`draft-no-owner-${draft.id}`}>No owner</Badge>
              )}
              {view === 'products' && draft.assignee_id && (
                <Badge variant="outline" className="text-[11px]" data-testid={`draft-owner-${draft.id}`}>Owner: {draft.assignee_name || 'Unknown'}</Badge>
              )}
              {draft.tier && (
                <Badge variant="outline" className={badgeClass(VENDOR_TIER_BADGE, draft.tier)} data-testid={`draft-vendor-tier-${draft.id}`}>
                  {VENDOR_TIER_LABEL[draft.tier] || draft.tier}
                </Badge>
              )}
              {draft.low_qty_only && (
                <Badge variant="outline" className="text-[11px] border-amber-500/40 bg-amber-500/10 text-amber-700" data-testid={`draft-low-qty-only-${draft.id}`}>Low qty only</Badge>
              )}
              {view === 'all' && canEdit && (
                <Button size="sm" variant="outline" onClick={() => openReassign(draft)} data-testid={`draft-reassign-btn-${draft.id}`}>Reassign</Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {draft.lines.map((line) => (
              <div key={line.id} className="flex items-center gap-3 border border-border rounded-sm p-2 flex-wrap" data-testid={`draft-line-${line.id}`}>
                {draft.status === 'OPEN' && draft.can_send && (
                  <Checkbox
                    checked={(selected[draft.id] || new Set()).has(line.id)}
                    onCheckedChange={() => toggleLine(draft.id, line.id)}
                    data-testid={`draft-line-checkbox-${line.id}`}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-mono truncate">{line.search_part} <span className="text-muted-foreground">({line.sku_id})</span></p>
                  <p className="text-xs text-muted-foreground">Qty: {line.qty}</p>
                  <p className="text-xs text-muted-foreground" data-testid={`draft-line-prices-${line.id}`}>
                    Vendor price: <span data-testid={`draft-line-vendor-price-${line.id}`}>{line.vendor_price != null ? `$${line.vendor_price}` : '-'}</span>
                    {' · '}Our target: <span data-testid={`draft-line-target-price-${line.id}`}>{line.target_price_usd != null ? `$${line.target_price_usd}` : '-'}</span>
                  </p>
                </div>
                {line.below_min_qty && (
                  <Badge variant="outline" className="text-[11px] border-amber-500/40 bg-amber-500/10 text-amber-700 shrink-0" data-testid={`draft-line-low-qty-${line.id}`}>
                    Low qty (below {line.min_qty_threshold ?? 10})
                  </Badge>
                )}
                {draft.status === 'OPEN' && draft.can_send && (selected[draft.id] || new Set()).has(line.id) && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Input type="number" className="w-20 h-8" placeholder={String(line.qty)}
                      value={qtyOverrides[line.id] ?? ''} onChange={(e) => setQtyOverrides({ ...qtyOverrides, [line.id]: e.target.value })}
                      data-testid={`draft-line-qty-override-${line.id}`} />
                    <label className="flex items-center gap-1 text-xs">
                      <Checkbox checked={!!priceLines[line.id]} onCheckedChange={(v) => togglePriceLine(line, v)}
                        data-testid={`draft-line-send-price-checkbox-${line.id}`} /> Send our price
                    </label>
                    {priceLines[line.id] && (
                      <Input type="number" min="0" step="0.01" className="w-24 h-8" placeholder="Price"
                        value={priceOverrides[line.id] ?? ''} onChange={(e) => setPriceOverrides({ ...priceOverrides, [line.id]: e.target.value })}
                        data-testid={`draft-line-send-price-input-${line.id}`} />
                    )}
                  </div>
                )}
              </div>
            ))}
            {draft.status === 'OPEN' && draft.can_send && (
              <Button size="sm" className="w-full sm:w-auto" onClick={() => openSend(draft)} data-testid={`draft-send-btn-${draft.id}`}>
                <Send className="h-3.5 w-3.5 mr-1.5" /> Send RFQ
              </Button>
            )}
          </CardContent>
        </Card>
      ))}

      <Dialog open={!!sendDraft} onOpenChange={(o) => !o && setSendDraft(null)}>
        <DialogContent data-testid="draft-send-dialog">
          <DialogHeader><DialogTitle>Send RFQ to {sendDraft?.company_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {sendPreview?.on_behalf && (
              <div className="rounded-sm border border-sky-500/40 bg-sky-500/10 p-3 text-xs text-sky-700" data-testid="draft-send-onbehalf-banner">
                {sendPreview.login_used ? (
                  <>
                    Sending on behalf of {sendPreview.assignee_name} (BrokerBin login: {sendPreview.login_used})
                    {sendPreview.used_own_login && ' — your own login, the assignee has none mapped'}
                  </>
                ) : (
                  <>Sending on behalf of {sendPreview.assignee_name} — no BrokerBin login mapped for either of you, sending will fail. Ask Admin.</>
                )}
              </div>
            )}
            <Textarea placeholder="Optional note" value={note} onChange={(e) => setNote(e.target.value)} data-testid="draft-send-note" />
            {lastBlocked && lastBlocked.length > 0 && (
              <div className="rounded-sm border border-amber-500/40 bg-amber-500/10 p-3 space-y-1" data-testid="draft-send-cooldown-warning">
                <p className="text-xs font-medium flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /> On cooldown, not sent:</p>
                {lastBlocked.map((b) => (
                  <p key={b.line_id} className="text-xs text-amber-700" data-testid={`draft-send-cooldown-reason-${b.line_id}`}>
                    {b.sku_id}: {b.reason}
                  </p>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDraft(null)}>Close</Button>
            <Button onClick={confirmSend} disabled={sending} data-testid="draft-send-confirm-btn">{sending ? 'Sending...' : 'Send'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reassignDraft} onOpenChange={(o) => !o && setReassignDraft(null)}>
        <DialogContent data-testid="draft-reassign-dialog">
          <DialogHeader><DialogTitle>Reassign {reassignDraft?.company_name}</DialogTitle></DialogHeader>
          <Input placeholder="Search a Trade IQ user..." value={reassignQuery} onChange={(e) => setReassignQuery(e.target.value)} data-testid="draft-reassign-search" />
          <div className="max-h-48 overflow-y-auto divide-y divide-border">
            {reassignResults.map((u) => (
              <div key={u.id} className="px-2 py-2 text-sm hover:bg-muted cursor-pointer" onClick={() => doReassign(u.id)} data-testid={`draft-reassign-option-${u.id}`}>
                {u.name} <span className="text-muted-foreground text-xs">{u.email}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => doReassign(null)} data-testid="draft-reassign-unassign-btn">Unassign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
